import React, { useCallback, useEffect, useState } from 'react'
import {
  ConversationAction,
  ConversationData,
  ConversationMessage,
} from '@chilipiper/api-client/src/chat-platform-chat'
import {
  type BlockOrCompositeBlock,
  BotLauncher,
  BotPreview,
  Chat,
  useAppearanceSettings,
  useMessageFeedContext,
  useXMPPClientContext,
} from '@chilipiper/chat-components'
import { useMutation } from '@tanstack/react-query'
import { defaultErrorCallback } from '@chilipiper/service/src/api'
import { useCollectEvent } from './api-queries/chatPlatformChat'
import { useSyncIframeSize } from './hooks'
import { sendMessageToEmbed } from './utils/embedCommunication'

type BotState = 'preview' | 'collapsed' | 'expanded'

interface Params {
  action?: ConversationAction
  botState: BotState
  hasPreviewBlocks: boolean
  lastBlock?: BlockOrCompositeBlock
  message?: ConversationMessage[]
}

const extractBotState = ({ botState, hasPreviewBlocks, lastBlock, message }: Params) => {
  const shouldPreview =
    botState === 'preview' &&
    hasPreviewBlocks &&
    message?.every(message => message.type === 'Content' || message.type === 'PiperAiContent')
  const shouldExpand = lastBlock?.type === 'LiveMessage' || botState === 'expanded'
  return {
    shouldPreview,
    shouldExpand,
  }
}

export const Chatbot = ({
  workspaceId,
  guestId,
  conversationData,
  isBootstrapped,
  playbookId,
  onSessionRestart,
  botState,
  onBotStateChange,
  isRestarting,
}: {
  conversationData?: ConversationData[]
  guestId: string
  isBootstrapped: boolean
  onSessionRestart: (options?: { force: boolean }) => void
  playbookId?: string
  workspaceId: string
  botState: BotState
  onBotStateChange: (botState: BotState) => void
  isRestarting?: boolean
}) => {
  const [botConnectionError, setBotConnectionError] = useState(false)
  const [replyButtonIndex, setReplyButtonIndex] = useState<number | undefined>()
  const [messageToSend, setMessageToSend] = useState<string | undefined>()
  const {
    blocks,
    isLoading: isPreparingChat,
    pickReplyButton,
    sendMessageToPiperAi,
    prepareChat,
    tenantId,
    reconnectingStatus,
  } = useMessageFeedContext()
  const { client } = useXMPPClientContext()
  const { mutate: collect } = useCollectEvent(tenantId, guestId)
  const {
    mutate: pickButton,
    isPending: isPickingButton,
    isSuccess: isPickingButtonSuccess,
  } = useMutation({
    mutationFn: async (data: Parameters<typeof pickReplyButton>[0]) =>
      pickReplyButton(data) ?? null,
  })

  const {
    mutate: sendMessage,
    isPending: isSendingMessage,
    isSuccess: isSendingMessageSuccess,
  } = useMutation({
    mutationFn: async (data: Parameters<typeof sendMessageToPiperAi>[0]) =>
      sendMessageToPiperAi(data) ?? null,
  })

  useEffect(() => {
    if (
      replyButtonIndex !== undefined &&
      reconnectingStatus === 'CONNECTED' &&
      !isPreparingChat &&
      !isPickingButton &&
      !isPickingButtonSuccess
    ) {
      pickButton(replyButtonIndex)
    }
  }, [
    replyButtonIndex,
    isPreparingChat,
    isPickingButton,
    isPickingButtonSuccess,
    pickButton,
    reconnectingStatus,
  ])

  useEffect(() => {
    if (
      messageToSend !== undefined &&
      reconnectingStatus === 'CONNECTED' &&
      !isPreparingChat &&
      !isSendingMessage &&
      !isSendingMessageSuccess
    ) {
      sendMessage(messageToSend)
    }
  }, [
    isPreparingChat,
    isSendingMessage,
    isSendingMessageSuccess,
    messageToSend,
    reconnectingStatus,
    sendMessage,
  ])

  const appearanceSettings = useAppearanceSettings()
  const previewContainerRef = useSyncIframeSize()
  const handleClose = () => {
    resizeToBotState('collapsed')
    /** Wait a bit for a resize before changing state for more fluid transition */
    setTimeout(() => {
      onBotStateChange('collapsed')
    }, 100)
    collect({ type: 'WidgetClosed' })
  }

  const data = conversationData?.[0]

  const resizeToBotState = useCallback(
    (botState: BotState) => {
      switch (botState) {
        case 'expanded': {
          const size = {
            height: `calc(100% - 52px - ${appearanceSettings.bottomSpacing}px)`,
            width: '400px',
          }

          sendMessageToEmbed({
            type: 'SET_SIZE',
            value: size,
          })
          sendMessageToEmbed({
            type: 'WIDGET_STATE_CHANGE',
            value: 'expanded',
          })
          break
        }
        case 'collapsed': {
          const size = { height: '75px', width: '75px' }
          sendMessageToEmbed({
            type: 'SET_SIZE',
            value: size,
          })
          sendMessageToEmbed({
            type: 'WIDGET_STATE_CHANGE',
            value: 'collapsed',
          })
          break
        }
      }
    },
    [appearanceSettings.bottomSpacing]
  )

  useEffect(() => {
    resizeToBotState(botState)
  }, [botState, resizeToBotState])

  useEffect(() => {
    if (
      isBootstrapped &&
      botState === 'expanded' &&
      playbookId &&
      reconnectingStatus === 'DISCONNECTED'
    ) {
      prepareChat({
        guestId,
        workspaceId,
        playbookId,
        tenantId,
      }).catch(e => {
        defaultErrorCallback(e)
        setBotConnectionError(true)
      })
    }
  }, [
    isBootstrapped,
    botState,
    tenantId,
    playbookId,
    guestId,
    reconnectingStatus,
    prepareChat,
    workspaceId,
  ])

  const isReplyButtonsPreview = Boolean(
    (data?.messages?.length ?? 0) > 0 &&
      data?.messages?.every(message => message.type === 'Content') &&
      data.action?.type === 'SelectReplyAction'
  )
  const isAiPreview = Boolean(
    (data?.messages?.length ?? 0) > 0 &&
      data?.messages?.every(message => message.type === 'PiperAiContent') &&
      data.action?.type === 'EnterPiperAiChat'
  )

  if (!isBootstrapped || botConnectionError) {
    return null
  }
  const lastBlock = blocks?.at(-1)

  const hasPreviewBlocks = isReplyButtonsPreview || isAiPreview
  const { shouldPreview, shouldExpand } = extractBotState({
    botState,
    lastBlock,
    hasPreviewBlocks,
    message: data?.messages,
  })

  if (!shouldExpand && !shouldPreview && botState !== 'collapsed') {
    onBotStateChange('collapsed')
  }

  if (data && shouldPreview) {
    return (
      <BotPreview
        ref={previewContainerRef}
        onClose={handleClose}
        onCallToActionClick={() => onBotStateChange('expanded')}
        onReplyButtonClick={(_, index) => {
          setReplyButtonIndex(index)
          client.registerEvent({
            ...data,
            author: 'bot',
            timestamp: new Date().toISOString(),
            type: 'ConversationData',
            roomId: 'initial',
            sent: false,
            statuses: {},
          })
          client.registerEvent({
            type: 'ConversationData',
            messages: [{ type: 'AcceptedInput', value: { type: 'ReplyButtonSelected', index } }],
            action: { type: 'TerminateConversation' },
            roomId: 'initial',
            sent: false,
            statuses: {},
            timestamp: new Date(Date.now() + 100).toISOString(),
            author: 'bot',
            id: 'initial-action',
          })
          onBotStateChange('expanded')
        }}
        onSendMessage={message => {
          setMessageToSend(message)
          client.registerEvent({
            ...data,
            author: 'bot',
            timestamp: new Date().toISOString(),
            type: 'ConversationData',
            roomId: 'initial',
            sent: false,
            statuses: {},
          })
          client.registerEvent({
            type: 'MessageToPiperAi',
            value: { content: message },
            roomId: 'initial',
            sent: false,
            statuses: {},
            timestamp: new Date(Date.now() + 100).toISOString(),
            author: `${guestId}_guest`,
            id: 'initial-action',
          })
          onBotStateChange('expanded')
        }}
        messages={data.messages}
        action={data.action}
      />
    )
  }
  if (botState === 'collapsed') {
    return <BotLauncher ref={previewContainerRef} onClick={() => onBotStateChange('expanded')} />
  }
  if (shouldExpand) {
    return (
      <Chat
        onChatClose={handleClose}
        onSessionRestart={onSessionRestart}
        isRestarting={isRestarting}
      />
    )
  }

  return null
}
